import React from "react";
import { Link } from "react-router-dom";
import { PAYMENT_PAGE_LINKS } from "../data/paymentPages";

const GROUPS = [
  {
    title: "Brokerage & Forex",
    to: "/brokerage-launch",
    terms: [
      "Start a forex brokerage", "Forex brokerage setup", "White label forex broker", "MT5 white label",
      "Forex broker licence", "Brokerage launch consultancy", "CFD broker setup", "Crypto brokerage setup",
      "Multi-asset broker setup", "Forex broker startup cost", "Brokerage business plan", "STP / ECN broker model",
    ],
  },
  {
    title: "Prop Trading Firms",
    to: "/prop-firm-launch",
    terms: [
      "How to start a prop firm", "Prop firm setup", "Prop trading firm launch", "Funded trader program",
      "Prop firm challenge model", "Prop firm payout reserve", "Prop firm regulation", "White label prop firm",
      "Prop firm technology", "Futures prop firm setup", "Prop firm risk management", "Prop firm pricing",
    ],
  },
  {
    title: "Regulation, Licensing & Incorporation",
    to: "/incorporation",
    terms: [
      "Offshore company incorporation", "Forex licence cost", "Seychelles securities dealer licence",
      "Mauritius investment dealer licence", "Saint Lucia company", "Saint Vincent company", "BVI incorporation",
      "Cayman incorporation", "Dubai DMCC / DIFC setup", "CySEC licence support", "South Africa FSCA licence",
      "Broker licensing & regulation", "Choosing the right regulator", "Licence application support",
    ],
  },
  {
    title: "Payments & Corporate Banking",
    to: "/payments",
    terms: [
      "Corporate bank account for brokers", "Corporate banking for prop firms", "EMI account for forex broker",
      "Card payment processing for brokers", "Card payment providers", "High-risk payment processing",
      "PSP for forex brokers", "Merchant account for trading", "Payment gateway integration",
      "Crypto payment gateway", "Chargeback & 3-DS setup", "Payment processing and banking support",
      "IBAN for brokers", "Virtual IBANs", "UAE corporate bank account", "EU EMI IBAN", "SEPA & SEPA Instant", "SWIFT wires",
      "PIX payments Brazil", "SPEI Mexico", "PSE Colombia", "Apple Pay & Google Pay", "Credit card deposits", "USDT deposits & payouts",
    ],
  },
  {
    title: "Compliance & AML",
    to: "/legal-compliance",
    terms: [
      "Compliance support for brokers", "AML compliance forex", "Outsourced MLRO", "DMLRO services",
      "KYC / AML policy", "MiFID compliance", "Regulatory reporting", "Financial regulation advisory",
      "AML risk assessment", "Client onboarding & KYC", "Regulator liaison", "Compliance through our associates",
    ],
  },
  {
    title: "Trading Technology",
    to: "/trading-platforms",
    terms: [
      "MetaTrader 5 setup", "cTrader white label", "Match-Trader setup", "Forex CRM & back office",
      "Liquidity providers for brokers", "Liquidity bridge setup", "IB / affiliate system", "Client portal",
      "Trading platform comparison", "Fintech consulting", "Financial market industry consulting", "Trading technology stack",
    ],
  },
];

const REGIONS = [
  ["brokerage-launch-dubai", "Brokerage Setup Dubai"],
  ["eu-brokerage-setup", "EU Brokerage Setup"],
  ["brokerage-setup-asia", "Brokerage Setup Asia"],
  ["brokerage-setup-south-africa", "Brokerage Setup South Africa"],
  ["brokerage-setup-india", "Brokerage Setup India"],
  ["brokerage-setup-nigeria", "Brokerage Setup Nigeria"],
  ["brokerage-setup-kenya", "Brokerage Setup Kenya"],
  ["brokerage-setup-indonesia", "Brokerage Setup Indonesia"],
  ["brokerage-setup-vietnam", "Brokerage Setup Vietnam"],
  ["brokerage-setup-philippines", "Brokerage Setup Philippines"],
  ["abrir-broker-latam", "Abrir un Bróker · LATAM"],
  ["crear-prop-firm", "Crear una Prop Firm"],
  ["licencia-forex-latinoamerica", "Licencia Forex · LATAM"],
];

export const SeoKeywordHub = () => (
  <section className="border-t border-white/5" data-testid="seo-keyword-hub" aria-label="Services we help founders with">
    <div className="max-w-7xl mx-auto px-5 sm:px-10 py-10">
      <h2 className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">What we help founders with</h2>
      <p className="mb-7 text-sm text-zinc-300 max-w-3xl leading-relaxed" data-testid="seo-hub-regulation-note">
        We help you with regulation from start to finish — choosing the right jurisdiction and licence, preparing the
        application, and keeping you compliant (AML, KYC, MLRO / DMLRO) through our associates once you are live.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <Link to={g.to} data-testid={`seo-hub-${g.to.slice(1)}`} className="text-sm text-zinc-200 hover:text-purple-300 transition-colors">
              {g.title}
            </Link>
            <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
              {g.terms.map((t, i) => (
                <React.Fragment key={t}>
                  <Link to={g.to} className="hover:text-zinc-300 transition-colors">{t}</Link>
                  {i < g.terms.length - 1 && <span className="text-zinc-700"> · </span>}
                </React.Fragment>
              ))}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-9" data-testid="seo-hub-payment-pages">
        <div className="text-sm text-zinc-200">Banking & payments</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PAYMENT_PAGE_LINKS.map(([slug, label]) => (
            <Link key={slug} to={`/${slug}`} data-testid={`seo-hub-pay-${slug}`}
              className="rounded-full border border-[#E5C56F]/25 bg-[#E5C56F]/[0.04] px-3 py-1.5 text-[11px] text-zinc-300 hover:text-[#FFE9A8] hover:border-[#E5C56F]/50 transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </div>
      <div className="mt-7" data-testid="seo-hub-regions">
        <div className="text-sm text-zinc-200">Regions we launch in</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {REGIONS.map(([slug, label]) => (
            <Link
              key={slug}
              to={`/${slug}`}
              data-testid={`seo-hub-region-${slug}`}
              className="rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[11px] text-zinc-400 hover:text-purple-200 hover:border-purple-400/40 transition-colors"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
      <p className="mt-6 text-[11px] leading-relaxed text-zinc-600 max-w-4xl">
        Serving founders in Latin America (Mexico, Colombia, Brazil, Chile, Argentina, Peru), the UAE and Gulf,
        Southeast Asia (Malaysia, Indonesia, Vietnam, Thailand, Philippines), India, Africa (South Africa, Nigeria, Kenya),
        and Europe — brokerage, forex, prop trading, payments, banking and compliance advisory.
      </p>
    </div>
  </section>
);
